import React, { ReactNode, useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';
import Icon, { COLORS, ICONS, IconType } from '../icon';
import { LazyMotion, domAnimation, m } from "framer-motion";
import '../../../scss/live-event/base/general-ui/button.scss';

type TTagName =
	| 'a'
	| 'button'
	| 'span'
	| 'div';

interface Props {
	children: ReactNode;
	classButton?: string;
	url?: string;
	template?: string;
	isRound?: boolean;
	isSmall?: boolean;
	tagName?: TTagName;
	type?: 'primary';
	typeBtn?: string;
	isDisabled?: boolean;
	onClick?: (e?: any) => void;
	icon?: { name: IconType };
	ariaLabel?: string;
	animate?: boolean;
	newTab?: boolean;
	title?: string;
	style?: React.CSSProperties;
}

export const Button = ({
	children,
	classButton,
	url,
	template,
	isRound,
	isSmall,
	tagName = 'button',
	type,
	typeBtn,
	isDisabled,
	onClick,
	icon,
	ariaLabel,
	animate = false,
	newTab = true,
	title,
	style
}: Props): JSX.Element => {
	const TagName: React.ElementType = tagName;
	const elementRef = useRef<HTMLElement | null>(null);
	const refocusRef = useRef(false);

	// restore focus lost while disabled
	useLayoutEffect(() => {
		if (isDisabled) {
			refocusRef.current = document.activeElement === elementRef.current;
			return;
		}
		if (refocusRef.current && document.activeElement === document.body) {
			elementRef.current?.focus();
		}
		refocusRef.current = false;
	}, [isDisabled]);

	if (animate && url) {
		console.warn('Cannot animate <Button /> as an anchor tag');
	}

	return animate ? (
		<LazyMotion features={domAnimation}>
			<m.button
				ref={elementRef as React.Ref<HTMLButtonElement>}
				transition={{
					type: "spring",
					stiffness: 500,
					damping: 10,
					velocity: 1.1
				}}
				style={style}
				whileTap={{ scale: 0.9 }}
				onClick={onClick}
				type={typeBtn as any}
				role={typeBtn === 'button' ? 'button' : undefined}
				aria-label={ariaLabel}
				aria-disabled={isDisabled}
				className={classNames({
					round: isRound,
					small: isSmall,
					primary: type === 'primary',
				}, classButton, template ? template + ' evt-button' : '', isDisabled ? ' disabled' : '')}
				disabled={isDisabled}
				title={title}
			>{icon && (
				<Icon name={ICONS[icon.name]} size={16} color={COLORS.WHITE} />
			)}
				{children}
			</m.button>
		</LazyMotion>
	) : (
		<TagName
			ref={elementRef as React.Ref<any>}
			onClick={isDisabled ? undefined : onClick}
			href={url && !isDisabled ? url : undefined}
			type={typeBtn as any}
			role={typeBtn === 'button' ? 'button' : undefined}
			aria-label={ariaLabel}
			aria-disabled={isDisabled}
			target={tagName === "a" && newTab ? "_blank" : undefined}
			title={title}
			style={style}
			disabled={tagName === 'button' ? isDisabled : undefined}
			tabIndex={isDisabled && tagName !== 'button' ? -1 : undefined}
			className={classNames({
				round: isRound,
				small: isSmall,
				primary: type === 'primary',
			}, classButton, template ? template + ' evt-button' : '', isDisabled ? ' disabled' : '')}
		>
			{icon && (
				<Icon name={ICONS[icon.name]} size={16} color={COLORS.WHITE} />
			)}
			{children}
		</TagName>
	);
};
